import './ViewArea.scss';

import classNames from 'classnames';
import { isNumber } from '@sonar-software/sonar-ts-lib';
import { PeriscopeView } from '@periscopes';

import React, { PropsWithChildren, useEffect } from 'react';
import { useApplicationSidebar, useOnMount, usePeriscopeCount } from '@hooks';

function getLimit(limit: boolean | 1 | 2 | undefined): number {
	if (isNumber(limit)) {
		return limit;
	}

	return limit ? 2 : 0;
}

export type ViewDirection = 'horizontal' | 'vertical';

export interface ViewAreaProps {
	className?: string;
	direction?: ViewDirection;
	fullHeight?: boolean;
	fullWidth?: boolean;
	id?: string;
	collapseSidebar?: boolean;
	noPadding?: boolean;
	showPeriscopes?: boolean | 1 | 2;
	sidebar?: React.ReactNode;
}

export const ViewArea: React.FC<PropsWithChildren<ViewAreaProps>> = ({
	children,
	className,
	direction = 'horizontal',
	fullHeight,
	fullWidth,
	id,
	collapseSidebar,
	noPadding,
	showPeriscopes,
	sidebar
}) => {
	const periscopeCount = usePeriscopeCount();
	const { close, open, isClosed } = useApplicationSidebar();

	const periscopeLimit = getLimit(showPeriscopes);

	useOnMount(() => {
		if (collapseSidebar) {
			close();
		} else if (isClosed) {
			open();
		}
	});

	useEffect(() => {
		if (showPeriscopes && !collapseSidebar) {
			if (periscopeCount >= periscopeLimit) {
				close();
			} else {
				open();
			}
		}
	}, [ close, open, periscopeCount, periscopeLimit, showPeriscopes, collapseSidebar ]);

	const wrapperClasses = classNames('view-wrapper', {
		'full-height': fullHeight,
		'full-width': fullWidth || (showPeriscopes && periscopeCount),
		'no-padding': noPadding,
		'only-one-periscope': periscopeCount === 1,
		'view-horizontal': direction === 'horizontal',
		'view-vertical': direction === 'vertical'
	});

	const viewAreaClasses = classNames(
		'view-area',
		{ 'periscope-hidden': showPeriscopes && !periscopeCount },
		className
	);

	return (
		<>
			<div className={ wrapperClasses }>
				{ sidebar }
				<div
					className={ viewAreaClasses }
					id={ id }>
					{ children }
				</div>
			</div>
			{ showPeriscopes &&
				<div
					className='periscope-view-wrapper'
					style={ { flex: periscopeCount } }>
					<PeriscopeView skipOnMount />
				</div> }
		</>
	);
};
